import React from 'react';
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';

import { uploadBiz } from './coreapi';

type Props = {
  prefix: string;
  service: string;
  action: string;
  params?: any;
  accept?: string;
  label?: string;
  helperText?: string;
  disabled?: boolean;
  onSuccess?: (result: any) => void;
  onError?: (error: any) => void;
};

type State = {
  file?: File;
  uploading: boolean;
  error: string;
  success: string;
};

export default class BizFileField extends React.PureComponent<Props, State> {
  state: State = {
    uploading: false,
    error: '',
    success: '',
  };

  onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    this.setState({
      file,
      error: '',
      success: '',
    });
  };

  upload = async () => {
    const { file } = this.state;
    const { prefix, service, action, params = {}, onSuccess, onError } = this.props;

    if (!file) {
      this.setState({ error: 'Please select a file first.' });
      return;
    }

    this.setState({ uploading: true, error: '', success: '' });

    try {
      const result = await uploadBiz({
        prefix,
        service,
        action,
        file,
        params,
      });

      this.setState({
        uploading: false,
        success: 'File uploaded and processed successfully.',
        file: undefined,
      });

      onSuccess?.(result);
    } catch (e: any) {
      this.setState({
        uploading: false,
        error: e?.message || 'File upload failed.',
      });

      onError?.(e);
    }
  };

  render() {
    const {
      label = 'Upload File',
      helperText = 'Upload evidence, imports, or supporting documents.',
      accept,
      disabled,
    } = this.props;

    const { file, uploading, error, success } = this.state;

    return (
      <Paper elevation={0} sx={{ p: 2, border: '1px dashed', borderColor: 'divider' }}>
        <Stack spacing={1.5}>
          <Box>
            <Typography variant="subtitle2">{label}</Typography>
            <Typography variant="body2" color="text.secondary">
              {helperText}
            </Typography>
          </Box>

          {uploading && <LinearProgress />}

          {error && <Alert severity="error">{error}</Alert>}
          {success && <Alert severity="success">{success}</Alert>}

          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant="outlined"
              component="label"
              disabled={disabled || uploading}
              startIcon={<UploadFileIcon />}
            >
              Choose File
              <input
                hidden
                type="file"
                accept={accept}
                onChange={this.onFileChange}
              />
            </Button>

            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
              {file ? file.name : 'No file selected'}
            </Typography>

            <Button
              variant="contained"
              disabled={disabled || uploading || !file}
              onClick={this.upload}
            >
              Upload
            </Button>
          </Stack>
        </Stack>
      </Paper>
    );
  }
}